import { ToneOfVoiceCapturePayload } from "@/pages/Private/pages/Messages/components/GenerateBar/types";

import { Draft } from "./inboxDraft.types";

export interface SendMessageItem {
	message: string;
	subject?: string;
	scheduledInDays?: number;
	senderUserId?: number;
	senderEmailId?: number;
	senderType?: "email" | "linkedin";
	recipientType?: "email" | "linkedin";
	recipientEmail?: string;
	cc?: string[];
	bcc?: string[];
	isLinkedinInvite?: boolean;
	includeSignature?: boolean;
	toneOfVoice?: ToneOfVoiceCapturePayload;
}

/**
 * The delay a message is sent with. The first one goes now unless it was given a delay; a follow-up
 * waits at least a day — except a message that waits for the sender's pending invitation: its
 * "when they accept" is a 0 the API releases at the acceptance, and a 1 would hold it a day longer
 * (PREC-4216).
 */
const sendDelayDays = (draft: Draft, index: number, waitsForAcceptance: boolean) => {
	const days = draft.timing.scheduledInDays;

	if (index === 0) {
		return days || undefined;
	}

	return waitsForAcceptance ? (days ?? 0) : days || 1;
};

/**
 * Map Draft[] to the payload accepted by `POST /schedule/send-messages`.
 *
 * When a draft carries an `aiOrigin` snapshot (i.e. it was injected from an AI
 * generation), the snapshot is forwarded as `toneOfVoice` so the BE can capture
 * the diff between the original AI text and the (possibly edited) final text.
 * The metadata is attached unconditionally; the BE no-ops when text is unchanged.
 *
 * An email carries its Include signature choice, resolved here: a draft the user never toggled
 * takes the company default, so a later change of the setting does not touch a queued message
 * (PREC-4401). A LinkedIn message carries none. `waitsForAcceptance` names the messages parked behind
 * their sender's pending invitation (PREC-4216), whose delay counts from the acceptance.
 */
export const draftsToSendMessageItems = (
	drafts: Draft[],
	signatureDefault = false,
	waitsForAcceptance: (draft: Draft) => boolean = () => false
): SendMessageItem[] =>
	drafts.map((d, index) => {
		const item: SendMessageItem = {
			message: d.content,
			subject: d.from?.type === "email" ? d.subject || undefined : undefined,
			scheduledInDays: sendDelayDays(d, index, waitsForAcceptance(d)),
			senderUserId: d.from?.userId || undefined,
			senderEmailId: d.from?.emailId || undefined,
			senderType: d.from?.type as "email" | "linkedin" | undefined,
			recipientType: d.to?.type as "email" | "linkedin" | undefined,
			recipientEmail: d.to?.type === "email" ? d.to?.value || undefined : undefined,
			cc: d.cc?.length ? d.cc : undefined,
			bcc: d.bcc?.length ? d.bcc : undefined,
			isLinkedinInvite: d.isInviteNote || undefined,
			includeSignature:
				d.from?.type === "email" ? (d.includeSignature ?? signatureDefault) : undefined,
		};

		if (d.aiOrigin) {
			item.toneOfVoice = d.aiOrigin;
		}

		return item;
	});
